import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { orgHref } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { Button, Card, Choice, Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { GiftFigure } from './StopGiftPage'
import './gift.css'

/**
 * The page the letter confirming a monthly or yearly gift opens. It goes to an
 * address already on an account, and following it is the proof that the
 * address is the giver's: confirming files the gift on that account and starts
 * it (product owner, 2026-10-11). No login: the `?token=` is the whole
 * authority, read on the page of the org the gift belongs to.
 *
 * Where the address is on several accounts, whoever follows the link holds
 * it and chooses which the gift is for (product owner, 2026-10-25).
 *
 * Confirming twice is the same as once, and a gift already set up says so. An
 * expired or broken link says what the server said about it.
 */
export function ConfirmGiftPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const link = { shortId: orgId, token }
  const [choice, setChoice] = useState<string | undefined>(undefined)

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const gift = useQuery({
    ...trpc.donation.giftConfirmView.queryOptions(link),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const confirm = useMutation({
    ...trpc.donation.confirmGift.mutationOptions(),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: trpc.donation.giftConfirmView.queryKey(link),
      }),
  })

  /* The button goes once it has done its work, so the sentence saying what
     happened takes the keyboard in its place. */
  const done = useRef<HTMLDivElement>(null)
  const setUp = gift.data ? !gift.data.awaitingConfirmation : false
  useEffect(() => {
    if (confirm.isSuccess && setUp) done.current?.focus()
  }, [confirm.isSuccess, setUp])

  if (org.isLoading || org.error || !org.data) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const o = org.data
  const view = gift.data ?? null

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">Confirm your gift</h1>
          {token && gift.isLoading ? (
            <p className="gift__muted">Loading…</p>
          ) : !token || gift.isError || !view ? (
            <Notice>
              {gift.error?.message ??
                'This link is not valid. Please use the link in the letter we sent, or contact the office.'}
            </Notice>
          ) : (
            <>
              <GiftFigure view={view} />
              {view.stopped ? (
                <Notice tone="info">
                  This gift is stopped. Your card will not be charged for it.
                </Notice>
              ) : !view.awaitingConfirmation ? (
                <div ref={done} tabIndex={-1}>
                  <Notice tone="info">
                    Your gift is set up. Each gift is receipted by email, with a
                    link to stop it.
                  </Notice>
                </div>
              ) : (
                <>
                  <p className="gift__muted">
                    Confirming sets the gift up on your account with us. Until
                    you do, nothing is charged.
                  </p>
                  {view.choices.length > 0 && (
                    <Choice
                      label="Which account is this gift for?"
                      value={choice}
                      onValueChange={setChoice}
                      options={view.choices.map((c) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                    />
                  )}
                  {confirm.error && (
                    <Notice tone="error">{confirm.error.message}</Notice>
                  )}
                  <Button
                    disabled={
                      confirm.isPending || (view.choices.length > 0 && !choice)
                    }
                    onClick={() => confirm.mutate({ ...link, choice })}
                  >
                    Confirm this gift
                  </Button>
                </>
              )}
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          )}
        </Card>
      </div>
    </PublicShell>
  )
}
